import React, { Fragment, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Breadcrumbs, ToolTip } from "../../AbstractElements";
import { Button, Card, CardBody, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import { useTranslation } from "react-i18next";
import { getSaltOnSearch, addNewItem, addSaltNameApi, createPlatformOTCItem } from "./PharmacyMedicineService";
import { useLoader } from "../../_helper/LoaderContext";
import { toast } from "react-toastify";
import { ArrowLeft, PlusCircle } from "react-feather";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import AsyncSelect from "react-select/async";
import { packageTypes, productForms, otcPackageTypes, otcProductForms } from "./UpdateMedicine";
import { useDisableScrollOnFocus } from "../../Hooks/useDisableScrollOnFocus";
import InfoIcon from "../PharmacyStocks/components/InfoIcon";
import { ADD_ITEMS } from "../PharmacyStocks/stoctTableInfo";
import { generateInternalEan13 } from "../../utils/internalEan13";
import {
	sanitizeBarcodeInput,
	BARCODE_FIELD_LENGTH,
} from "../../utils/barcodeUtils";
import CreatableOptionSelect from "./components/CreatableOptionSelect";
import {
	appendOptionalItemLists,
	getInteractionsList,
	getPrimaryUseList,
} from "./utils/itemListFields";
import {
	getItemMetaValue,
	getPackageLabel,
	getPackagingDetail,
	getProductForm,
} from "./utils/productMetaFields";
import { shouldShowItemTaxField } from "../PharmacyManageTax/taxConfigUtils";
import "./item-form.scss";

const AddItem = () => {
	const { t } = useTranslation();
	const history = useNavigate();
	const { setLoading } = useLoader();
	const { pharmacyPOS } = usePharmacy();

	const createItemBarcode = useCallback(
		() => generateInternalEan13(pharmacyPOS?.hospital_id),
		[pharmacyPOS?.hospital_id]
	);

	const [formData, setFormData] = useState({
		itemType: "medicine", // medicine | otc | unani
		brandName: "",
		saltName: "",
		saltId: "",
		manufacturer: "",
		package: "",
		productForm: "",
		quantity: "",
		regularPrice: "",
		sellingPrice: "",
		barcode: "",
		note: "",
		minAlertQuantity: "",
		currency: pharmacyPOS.baseCurrency || "INR",
		tax: "",
		primaryUse: [],
		interactions: [],
		packaging_detail: "",
		metaValue: "",
	});

	const isOTC = formData.itemType === "otc";
	const isUnani = formData.itemType === "unani";
	const isMedicineLike = formData.itemType === "medicine" || formData.itemType === "unani";
	const showTaxField = shouldShowItemTaxField(pharmacyPOS, formData.itemType);

	const [errors, setErrors] = useState({});
	const [selectedOption, setSelectedOption] = useState(null); // Track selected AsyncSelect option

	const disableScrollProps = useDisableScrollOnFocus();

	const [filterTooltipOpen, setFilterTooltipOpen] = useState(false);
	const toggleFilterTooltip = () => setFilterTooltipOpen(!filterTooltipOpen);

	// Modals state
	const [isSaltModalOpen, setSaltModalOpen] = useState(false);
	const [isOTCModalOpen, setOTCModalOpen] = useState(false);

	// OTC Modal states
	const [otcModalData, setOtcModalData] = useState({
		name: "",
		package: "",
		manufacturer: "",
		productForm: "",
		note: ""
	});

	// ✅ Load medicines on search - Medicine / Unani / OTC
	const loadMedicines = useCallback(async (inputValue) => {
		if (!inputValue || inputValue.length < 3) {
			return [];
		}

		try {
			setLoading(true);
			const body = {
				search: inputValue,
				type: isUnani ? "unani" : isOTC ? "otc" : "",
			};

			const response = await getSaltOnSearch(body);
			const medicines = response.data.data || [];

			return medicines.map((m) => {
				if (isOTC) {
					return {
						label: m.name || m.salt_composition || "",
						value: m._id,
						raw: m,
					};
				}
				return {
					label: m.name
						? `${m.name} ${m.salt_composition ? `[${m.salt_composition}]` : ""}`
						: m.salt_composition || m.salt_composition_new || "",
					value: m._id,
					raw: m,
				};
			});

		} catch (error) {
			console.error("Error fetching medicines:", error);
			toast.warning(t("Failed to search items"));
			return [];
		} finally {
			setLoading(false);
		}
	}, [isOTC, setLoading, t]);

	// Clear product fields only — keep barcode unless user clears it
	const clearFormData = useCallback(() => {
		setFormData((prev) => ({
			itemType: prev.itemType,
			brandName: "",
			saltName: "",
			saltId: "",
			manufacturer: "",
			package: "",
			productForm: "",
			quantity: "",
			regularPrice: "",
			sellingPrice: "",
			barcode: "",
			note: "",
			minAlertQuantity: prev.minAlertQuantity,
			currency: pharmacyPOS.baseCurrency || "INR",
			tax: "",
			primaryUse: [],
			interactions: [],
			packaging_detail: "",
			metaValue: "",
		}));
		setSelectedOption(null);
		setErrors({});
	}, [pharmacyPOS.baseCurrency]);

	// Handle medicine search change
	const handleMedicineSearchChange = useCallback((newValue) => {
		// Clear form when user starts typing a new search
		if (newValue && newValue.length >= 3) {
			clearFormData();
		}
	}, [clearFormData]);

	// ✅ When a medicine/OTC is selected
	const handleSelectMedicine = useCallback((selectedOption) => {
		setSelectedOption(selectedOption);

		if (!selectedOption) {
			clearFormData();
			return;
		}

		const medicine = selectedOption.raw || {};
		const newBarcode = createItemBarcode();

		setFormData((prev) => ({
			...prev,
			brandName: medicine.name || "",
			saltName: medicine.salt_composition || medicine.salt_composition_new || "",
			saltId: medicine._id || "",
			manufacturer: medicine.manufacturer || "",
			package: getPackageLabel(medicine),
			productForm: getProductForm(medicine),
			regularPrice: medicine.mrp || "",
			quantity: medicine.package_qty || medicine.quantity || "",
			note: medicine.note || "",
			tax: medicine.tax || "",
			barcode: newBarcode,
			primaryUse: getPrimaryUseList(medicine),
			interactions: getInteractionsList(medicine),
			packaging_detail: getPackagingDetail(medicine),
			metaValue: getItemMetaValue(medicine),
		}));
	}, [clearFormData, createItemBarcode]);

	// New draft when switching item type
	const handleItemTypeChange = (e) => {
		const { value } = e.target;
		setFormData({
			itemType: value,
			brandName: "",
			saltName: "",
			saltId: "",
			manufacturer: "",
			package: "",
			productForm: "",
			quantity: "",
			regularPrice: "",
			sellingPrice: "",
			barcode: "",
			note: "",
			minAlertQuantity: formData.minAlertQuantity,
			currency: pharmacyPOS.baseCurrency || "INR",
			tax: "",
			primaryUse: [],
			interactions: [],
			packaging_detail: "",
			metaValue: "",
		});

		setSelectedOption(null);
		setErrors({});
	};

	const handleRegenerateBarcode = () => {
		setFormData((prev) => ({ ...prev, barcode: createItemBarcode() }));
		setErrors((prev) => ({ ...prev, barcode: "" }));
	};

	// ✅ Handle input change
	const handleChange = (e) => {
		const { name, value } = e.target;
		if (name === "barcode") {
			setFormData((prev) => ({
				...prev,
				barcode: sanitizeBarcodeInput(value),
			}));
			if (errors.barcode) {
				setErrors((prev) => ({ ...prev, barcode: "" }));
			}
			return;
		}
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
		if (errors[name]) {
			setErrors((prev) => ({ ...prev, [name]: "" }));
		}
	};

	// ✅ Validation — brand/product name always; salt only for Medicine / Unani
	const validate = () => {
		const newErrors = {};

		if (!formData.brandName?.trim()) {
			newErrors.brandName = isOTC
				? t("Product name is required")
				: t("Brand name is required");
		}
		// if (isMedicineLike && !formData.saltName?.trim()) {
		// 	newErrors.saltName = t("Salt name / composition is required");
		// }

		return newErrors;
	};

	// ✅ Submit add
	const handleSubmit = async (e) => {
		e.preventDefault();
		const validationErrors = validate();
		if (Object.keys(validationErrors).length > 0) {
			setErrors(validationErrors);
			toast.warning(t("Please fill in all required fields"));
			return;
		}
		try {
			setLoading(true);

			const body = {
				clinicId: pharmacyPOS?.hospital_id,
				isOTC,
				isUnani,
				brandName: formData.brandName,
				manufacturer: formData.manufacturer,
				package: formData.package,
				productForm: formData.productForm,
				packageQuantity: formData.quantity,
				regularPrice: formData.regularPrice,
				barcode: formData.barcode,
				note: formData.note,
				minQuantityAlert: formData.minAlertQuantity,
				currency: formData.currency,
				...(showTaxField ? { tax: formData.tax } : {}),
			};

			if (isMedicineLike) {
				body.saltName = formData.saltName;
				body.medicationId = formData.saltId;
			} else {
				// OTC - use brandName as product name
				body.saltName = formData.saltName || formData.brandName;
			}

			appendOptionalItemLists(body, {
				primary_use: formData.primaryUse,
				interactions: formData.interactions,
				packaging_detail: formData.packaging_detail,
				value: formData.metaValue,
			});

			const response = await addNewItem(body);
			if (response.data.status === "success") {
				toast.success(response.data.message);
				gotoList();
			} else {
				toast.warning(response.data.message || t("Failed to add item"), {
					toastId: "add-item-error",
				});
			}
		} catch (error) {
			console.error("Error adding item:", error.response?.data?.message);
		} finally {
			setLoading(false);
		}
	};

	const gotoList = () => {
		history(`${process.env.PUBLIC_URL}/items`);
	};

	// For Medicine modal (salt names)
	const [saltRows, setSaltRows] = useState([{ brandName: "", saltName: "" }]);
	const [productForm, setProductForm] = useState("");
	const [packageType, setPackageType] = useState("");
	const [manufacturer, setManufacturer] = useState("");
	const [manufacturerAddress, setManufacturerAddress] = useState("");
	const [primaryUses, setPrimaryUses] = useState([""]);
	const [interactions, setInteractions] = useState([""]);
	const [packaging_detail, setPackagingDetail] = useState("");
	const [saltModalError, setSaltModalError] = useState("");
	const [isSaltSubmitting, setSaltSubmitting] = useState(false);

	// For OTC modal
	const [otcModalError, setOtcModalError] = useState("");
	const [isOTCSubmitting, setOTCSubmitting] = useState(false);

	const normalizeStringList = (values) =>
		(values || []).map((value) => String(value || "").trim()).filter(Boolean);

	// Medicine Modal Functions
	const addSaltRow = () => {
		setSaltRows([...saltRows, { saltName: "" }]);
	};

	const removeSaltRow = (index) => {
		const updated = saltRows.filter((_, i) => i !== index);
		setSaltRows(updated.length ? updated : [{ saltName: "" }]);
	};

	const handleSaltRowChange = (index, field, value) => {
		const updated = [...saltRows];
		if (field === "brandName" && index > 0) {
			updated[0][field] = value;
		} else {
			updated[index][field] = value;
		}
		setSaltRows(updated);
	};

	const addListRow = (setter) => {
		setter((prev) => [...prev, ""]);
	};

	const removeListRow = (setter, index) => {
		setter((prev) => {
			const updated = prev.filter((_, i) => i !== index);
			return updated.length ? updated : [""];
		});
	};

	const handleListRowChange = (setter, index, value) => {
		setter((prev) => {
			const updated = [...prev];
			updated[index] = value;
			return updated;
		});
	};

	const toggleSaltModal = () => {
		setSaltModalOpen(!isSaltModalOpen);
		setSaltModalError("");
		setSaltRows([{ brandName: "", saltName: "" }]);
		setPackageType("");
		setProductForm("");
		setManufacturer("");
		setManufacturerAddress("");
		setPrimaryUses([""]);
		setInteractions([""]);
		setPackagingDetail("");
	};

	const handleSaltModalSubmit = async (e) => {
		e.preventDefault();
		setSaltModalError("");

		if (!saltRows[0]?.brandName?.trim()) {
			setSaltModalError(t("Brand name is required"));
			return;
		}

		const saltNames = saltRows
			.map((row) => row.saltName.trim())
			.filter(Boolean);
		const saltname_without_dosage = saltNames.join(" + ");
		const saltname_with_dosage = saltNames.join(" + ");
		const body = {
			salt_composition: saltname_with_dosage,
			salt_composition_new: saltname_without_dosage,
			package: packageType,
			product_form: productForm,
			name: saltRows[0].brandName.trim(),
			manufacturer: manufacturer.trim(),
			manufacturer_address: manufacturerAddress.trim(),
			primary_use: normalizeStringList(primaryUses),
			interactions: normalizeStringList(interactions),
			packaging_detail: packaging_detail.trim() || "",
			...(isUnani ? { type: "unani" } : {}),
		};

		try {
			setLoading(true);
			setSaltSubmitting(true);
			const response = await addSaltNameApi(body);

			if (response.data.status === "success") {
				toast.success(response.data.message);
				toggleSaltModal();
				setSelectedOption(null);
				clearFormData();
			} else {
				setSaltModalError(response.data.message || t("Failed to add medicine"));
			}
		} catch (error) {
			console.error("Error adding salt:", error);
			if (!error.response) {
				setSaltModalError(t("Failed to add medicine"));
			}
		} finally {
			setLoading(false);
			setSaltSubmitting(false);
		}
	};

	// OTC Modal Functions
	const toggleOTCModal = () => {
		setOTCModalOpen(!isOTCModalOpen);
		setOtcModalError("");
		setOtcModalData({
			name: "",
			package: "",
			manufacturer: "",
			productForm: "",
			note: ""
		});
	};

	const handleOTCModalChange = (e) => {
		const { name, value } = e.target;
		setOtcModalData(prev => ({
			...prev,
			[name]: value
		}));
	};

	const handleOTCModalSubmit = async (e) => {
		e.preventDefault();

		if (!otcModalData.name.trim()) {
			setOtcModalError(t("Product name is required"));
			return;
		}

		try {
			setLoading(true);
			setOTCSubmitting(true);

			const body = {
				name: otcModalData.name,
				manufacturer: otcModalData.manufacturer,
				package: otcModalData.package,
				product_form: otcModalData.productForm,
				salt_composition: otcModalData.name,
				salt_composition_new: otcModalData.name,
				note: otcModalData.note,
				type: "otc"
			};

			const response = await createPlatformOTCItem(body);

			if (response.data.status === "success") {
				toast.success(response.data.message || t("OTC product created successfully"));
				toggleOTCModal();
				// Clear the search to force refresh
				setSelectedOption(null);
				clearFormData();
			}
		} catch (error) {
			console.error("Error creating OTC product:", error);
			// toast.warning(error.response?.data?.message || t("Failed to create OTC product"));
		} finally {
			setLoading(false);
			setOTCSubmitting(false);
		}
	};

	// Helper functions
	const getSearchPlaceholder = () => {
		if (isOTC) return t("Search OTC products by name, manufacturer, etc.");
		if (isUnani) return t("Search Unani medicines by name or salt composition...");
		return t("Search medicines by name or salt composition, etc.");
	};

	const getNoOptionsMessage = ({ inputValue }) => {
		const query = (inputValue || "").trim();
		if (selectedOption && query.length < 3) {
			return t("Type to search for a different product");
		}
		if (query.length < 3) {
			return t("Type at least 3 characters to search...");
		}
		if (isOTC) return t("No OTC products found. Click + to add a new one.");
		if (isUnani) return t("No Unani medicines found. Click + to add a new one.");
		return t("No medicines found. Click + to add a new one.");
	};

	const isDark = document.body.classList.contains("dark-only");

	return (
		<Fragment>
			<Breadcrumbs mainTitle={t("Add Item")} parent={t("Dashboard")} title={t("Add Item")} />
			<Row className="mt-2 item-form-page">
				<Col sm={12}>
					<Card className="item-form-card">
						<CardBody className="item-form-body">
							<form onSubmit={handleSubmit} className="item-form">
								{/* Toolbar: type + search + back */}
								<div className="item-form-toolbar">
									<div className="item-form-type">
										<label className="form-label">{t("Item Type")}</label>
										<div className="item-type-switch">
											<label className="item-type-option">
												<input
													type="radio"
													name="itemType"
													value="medicine"
													checked={formData.itemType === "medicine"}
													onChange={handleItemTypeChange}
												/>
												<span title={t("Medicine")}>{t("Medicine")}</span>
											</label>
											<label className="item-type-option">
												<input
													type="radio"
													name="itemType"
													value="unani"
													checked={formData.itemType === "unani"}
													onChange={handleItemTypeChange}
												/>
												<span title={t("Unani Medicine")}>{t("U Medicine")}</span>
											</label>
											<label className="item-type-option">
												<input
													type="radio"
													name="itemType"
													value="otc"
													checked={formData.itemType === "otc"}
													onChange={handleItemTypeChange}
												/>
												<span title="OTC">OTC</span>
											</label>
										</div>
									</div>

									<div className="item-form-search">
										<label className="form-label label-with-info">
											{isOTC ? t("Search Product") : isUnani ? t("Search U Medicine") : t("Search Medicine")}
											<InfoIcon text={isOTC ? ADD_ITEMS.searchOTC : ADD_ITEMS.searchMedicine} />
										</label>
										<div className="input-group">
											<AsyncSelect
												className="flex-grow-1"
												cacheOptions
												loadOptions={loadMedicines}
												defaultOptions={[]}
												onChange={handleSelectMedicine}
												onInputChange={handleMedicineSearchChange}
												placeholder={getSearchPlaceholder()}
												noOptionsMessage={getNoOptionsMessage}
												isClearable={true}
												value={selectedOption}
												styles={{
													container: (base) => ({ ...base, flex: 1 }),
													control: (base) => ({
														...base,
														minHeight: "36px",
														fontSize: "0.875rem",
														borderRadius: "8px 0 0 8px",
														borderColor: "#d0d5dd",
													}),
												}}
												theme={(theme) => ({
													...theme,
													colors: {
														...theme.colors,
														primary25: isDark ? "#373737" : "#e6f7ffff",
													},
												})}
												key={formData.itemType}
											/>
											<div className="input-group-append" style={{ cursor: "pointer" }}>
												<span
													className="input-group-text cursor-pointer"
													id="FilterToolTip"
													onClick={isOTC ? toggleOTCModal : toggleSaltModal}
												>
													<PlusCircle size={16} />
												</span>
												<ToolTip
													attrToolTip={{
														placement: "top",
														isOpen: filterTooltipOpen,
														target: "FilterToolTip",
														toggle: toggleFilterTooltip,
													}}
												>
													<strong>
														{isOTC
															? t("Add New OTC Product")
															: t("Add New Brand Name")}
													</strong>
												</ToolTip>
											</div>
										</div>
									</div>

									<div className="item-form-toolbar-actions">
										<button
											type="button"
											className="btn btn-primary d-flex align-items-center item-form-back-btn"
											onClick={gotoList}
										>
											<ArrowLeft size={15} className="me-1" /> {t("Back")}
										</button>
									</div>
								</div>

								{/* Identity — larger Brand + Salt (Medicine / Unani) */}
								<div className="item-form-section">
									<div className="item-form-section-title">{t("Product Details")}</div>
									<div className="row g-2">
										<div className={isOTC ? "col-lg-7 col-md-8" : "col-lg-5 col-md-6"}>
											<label className="form-label">
												{isOTC ? t("Product Name") : t("Brand Name")}
												<span className="required-mark">*</span>
											</label>
											<input
												type="text"
												name="brandName"
												value={formData.brandName}
												onChange={handleChange}
												className={`form-control ${errors.brandName ? "is-invalid" : ""}`}
												placeholder={isOTC ? t("Enter product name") : t("Enter brand name")}
											/>
											{errors.brandName && <div className="text-danger field-error">{errors.brandName}</div>}
										</div>

										{isMedicineLike && (
											<div className="col-lg-5 col-md-6">
												<label className="form-label">
													{t("Salt Name / Composition")}
													{/* <span className="required-mark">*</span> */}
												</label>
												<input
													type="text"
													name="saltName"
													value={formData.saltName}
													onChange={handleChange}
													className={`form-control ${errors.saltName ? "is-invalid" : ""}`}
													placeholder={t("Enter salt name or composition")}
												/>
												{errors.saltName && <div className="text-danger field-error">{errors.saltName}</div>}
											</div>
										)}

										<div className={isOTC ? "col-lg-5 col-md-4" : "col-lg-2 col-md-12"}>
											<label className="form-label">{t("Manufacturer")}</label>
											<input
												type="text"
												name="manufacturer"
												value={formData.manufacturer}
												onChange={handleChange}
												className="form-control"
												placeholder={t("Manufacturer")}
											/>
										</div>
									</div>
								</div>

								<hr className="item-form-divider" />

								{/* Pack & price */}
								<div className="item-form-section">
									<div className="item-form-section-title">{t("Pack & Pricing")}</div>
									<div className="row g-2">
										<div className="col-lg-3 col-md-4 col-6">
											<label className="form-label label-with-info">
												{t("Packaging")}
												<InfoIcon text={ADD_ITEMS.packagingType} />
											</label>
											<CreatableOptionSelect
												options={isOTC ? otcPackageTypes : packageTypes}
												value={formData.package}
												onChange={(value) =>
													setFormData((prev) => ({ ...prev, package: value }))
												}
												placeholder={t("Search or type packaging")}
											/>
										</div>
										<div className="col-lg-3 col-md-4 col-6">
											<label className="form-label label-with-info">
												{t("Form")}
												<InfoIcon text={ADD_ITEMS.productForm} />
											</label>
											<CreatableOptionSelect
												options={isOTC ? otcProductForms : productForms}
												value={formData.productForm}
												onChange={(value) =>
													setFormData((prev) => ({ ...prev, productForm: value }))
												}
												placeholder={t("Search or type form")}
											/>
										</div>
										<div className="col-lg-2 col-md-4 col-6">
											<label className="form-label label-with-info">
												{t("Units / Pack")}
												<InfoIcon text={ADD_ITEMS.unitQuantity} />
											</label>
											<input
												type="number"
												name="quantity"
												min={1}
												value={formData.quantity}
												onChange={handleChange}
												className="form-control"
												{...disableScrollProps}
												placeholder="10"
											/>
										</div>
										<div className="col-lg-2 col-md-4 col-6">
											<label className="form-label">{t("Pack MRP")}</label>
											<input
												type="number"
												name="regularPrice"
												min="0"
												step="0.01"
												value={formData.regularPrice}
												onChange={handleChange}
												className="form-control"
												{...disableScrollProps}
												placeholder="0.00"
											/>
										</div>
										{showTaxField && (
											<div className="col-lg-1 col-md-4 col-6">
												<label className="form-label label-with-info">
													{t("Tax %")}
													<InfoIcon text={ADD_ITEMS.tax} />
												</label>
												<input
													type="number"
													name="tax"
													min={0}
													value={formData.tax}
													onChange={handleChange}
													className="form-control"
													{...disableScrollProps}
													placeholder="0"
												/>
											</div>
										)}
										<div className="col-lg-1 col-md-4 col-6">
											<label className="form-label">{t("Currency")}</label>
											<select
												name="currency"
												value={formData.currency}
												onChange={handleChange}
												className="form-select"
												disabled
											>
												<option value="INR">INR (₹)</option>
												<option value="USD">USD ($)</option>
												<option value="EUR">EUR (€)</option>
											</select>
										</div>
									</div>
								</div>

								{/* Stock, barcode, notes */}
								<div className="item-form-section mb-0">
									<div className="row g-2">
										<div className="col-lg-3 col-md-4">
											<label className="form-label label-with-info">
												{t("Min Stock Alert")}
												<InfoIcon text={ADD_ITEMS.minimumAlertQuantity} />
											</label>
											<input
												type="number"
												name="minAlertQuantity"
												min={0}
												value={formData.minAlertQuantity}
												onChange={handleChange}
												className="form-control"
												{...disableScrollProps}
												placeholder="0"
											/>
										</div>
										<div className="col-lg-4 col-md-4">
											<label className="form-label">{t("Bar Code")}</label>
											<div className="input-group">
												<input
													type="text"
													name="barcode"
													value={formData.barcode}
													onChange={handleChange}
													className="form-control"
													placeholder={t("13-digit barcode")}
													maxLength={BARCODE_FIELD_LENGTH}
													inputMode="numeric"
													autoComplete="off"
													{...disableScrollProps}
												/>
												<button
													type="button"
													className="btn btn-outline-secondary z-0"
													onClick={handleRegenerateBarcode}
													title={t("Generate barcode")}
												>
													<i className="fa fa-refresh" aria-hidden="true" />
												</button>
											</div>
										</div>
										<div className="col-lg-5 col-md-4">
											<label className="form-label">{t("Notes")}</label>
											<input
												type="text"
												name="note"
												value={formData.note}
												onChange={handleChange}
												className="form-control"
												placeholder={t("Optional notes")}
											/>
										</div>
									</div>
								</div>

								<div className="item-form-actions">
									<button className="btn btn-primary" type="submit">
										{t("Add Item")}
									</button>
									<Button color="secondary" onClick={gotoList}>
										{t("Cancel")}
									</Button>
								</div>
							</form>
						</CardBody>
					</Card>
				</Col>
			</Row>

			{/* Medicine (Salt) Modal */}
			<Modal isOpen={isSaltModalOpen} toggle={toggleSaltModal} size="lg" className="master-add-modal">
				<ModalHeader toggle={toggleSaltModal}>{ isUnani ? t("Add New Unani Medicine") : t("Add New Medicine")}</ModalHeader>
				<form onSubmit={handleSaltModalSubmit}>
					<ModalBody className="master-add-modal-body">
						<div className="row g-2">
							<div className="col-12">
								<label className="form-label label-with-info">
									{t("Brand Name")} <span className="required-mark">*</span>
									<InfoIcon text={t("Required. Enter the medicine brand name as it should appear in master.")} />
								</label>
								<input
									type="text"
									className="form-control form-control-sm"
									value={saltRows[0]?.brandName || ""}
									onChange={(e) => handleSaltRowChange(0, "brandName", e.target.value)}
									disabled={isSaltSubmitting}
									placeholder={t("e.g., Crocin, Combiflam")}
								/>
							</div>

							<div className="col-md-6">
								<label className="form-label label-with-info">
									{t("Manufacturer")}
									<InfoIcon text={t("Optional. Company that manufactures this medicine.")} />
								</label>
								<input
									type="text"
									className="form-control form-control-sm"
									value={manufacturer}
									onChange={(e) => setManufacturer(e.target.value)}
									disabled={isSaltSubmitting}
									placeholder={t("e.g., GSK, Cipla")}
								/>
							</div>
							<div className="col-md-6">
								<label className="form-label label-with-info">
									{t("Manufacturer Address")}
									<InfoIcon text={t("Optional. Manufacturing address for master records.")} />
								</label>
								<input
									type="text"
									className="form-control form-control-sm"
									value={manufacturerAddress}
									onChange={(e) => setManufacturerAddress(e.target.value)}
									disabled={isSaltSubmitting}
									placeholder={t("City, state, country")}
								/>
							</div>

							<div className="col-12">
								<div className="master-add-section-head">
									<label className="form-label label-with-info mb-0">
										{t("Salt Names")}
										<InfoIcon text={t("Optional. Add one or more salt / composition names for this brand.")} />
									</label>
									<Button
										color="success"
										size="sm"
										type="button"
										className="master-add-mini-btn"
										onClick={addSaltRow}
										disabled={isSaltSubmitting}
									>
										+ {t("Add")}
									</Button>
								</div>
								{saltRows.map((row, index) => (
									<div className="master-add-list-row" key={`salt-${index}`}>
										<input
											type="text"
											className="form-control form-control-sm"
											value={row.saltName}
											onChange={(e) => handleSaltRowChange(index, "saltName", e.target.value)}
											disabled={isSaltSubmitting}
											placeholder={
												saltRows.length > 1
													? `${t("Salt Name / Composition")} ${index + 1}`
													: t("e.g., Paracetamol 500mg")
											}
										/>
										{saltRows.length > 1 && (
											<Button
												color="danger"
												size="sm"
												type="button"
												className="master-add-mini-btn"
												onClick={() => removeSaltRow(index)}
												disabled={isSaltSubmitting}
											>
												{t("Remove")}
											</Button>
										)}
									</div>
								))}
							</div>

							<div className="col-md-4">
								<label className="form-label label-with-info">
									{t("Packaging Type")}
									<InfoIcon text={t("Optional. How the medicine is packed (Strip, Bottle, Box, etc.).")} />
								</label>
								<CreatableOptionSelect
									options={packageTypes}
									value={packageType}
									onChange={setPackageType}
									isDisabled={isSaltSubmitting}
									isCompact
									inModal
									placeholder={t("Search or type packaging")}
								/>
							</div>
							<div className="col-md-4">
								<label className="form-label label-with-info">
									{t("Product Form")}
									<InfoIcon text={t("Optional. Dosage form such as Tablet, Capsule, Syrup.")} />
								</label>
								<CreatableOptionSelect
									options={productForms}
									value={productForm}
									onChange={setProductForm}
									isDisabled={isSaltSubmitting}
									isCompact
									inModal
									placeholder={t("Search or type form")}
								/>
							</div>
							<div className="col-md-4">
								<label className="form-label label-with-info">
									{t("Packaging Detail")}
									<InfoIcon text={t("Optional. Additional details about the packaging (bottle of 30 ml Suspension etc.).")} />
								</label>
								<input
									type="text"
									className="form-control form-control-sm"
									value={packaging_detail}
									onChange={(e) => setPackagingDetail(e.target.value)}
									disabled={isSaltSubmitting}
									placeholder={t("e.g., 10 tablets, 30 ml, 20 g, etc.") }
								/>
							</div>
							<div className="col-md-6">
								<div className="master-add-section-head">
									<label className="form-label label-with-info mb-0">
										{t("Primary Use")}
										<InfoIcon text={t("Optional. Add uses as separate entries. Sent as an array of strings.")} />
									</label>
									<Button
										color="success"
										size="sm"
										type="button"
										className="master-add-mini-btn"
										onClick={() => addListRow(setPrimaryUses)}
										disabled={isSaltSubmitting}
									>
										+ {t("Add")}
									</Button>
								</div>
								{primaryUses.map((use, index) => (
									<div className="master-add-list-row" key={`primary-use-${index}`}>
										<input
											type="text"
											className="form-control form-control-sm"
											value={use}
											onChange={(e) => handleListRowChange(setPrimaryUses, index, e.target.value)}
											disabled={isSaltSubmitting}
											placeholder={t("e.g., Fever, Pain relief")}
										/>
										{primaryUses.length > 1 && (
											<Button
												color="danger"
												size="sm"
												type="button"
												className="master-add-mini-btn"
												onClick={() => removeListRow(setPrimaryUses, index)}
												disabled={isSaltSubmitting}
											>
												{t("Remove")}
											</Button>
										)}
									</div>
								))}
							</div>

							<div className="col-md-6">
								<div className="master-add-section-head">
									<label className="form-label label-with-info mb-0">
										{t("Interactions")}
										<InfoIcon text={t("Optional. Add drug/food interactions as separate entries. Sent as an array of strings.")} />
									</label>
									<Button
										color="success"
										size="sm"
										type="button"
										className="master-add-mini-btn"
										onClick={() => addListRow(setInteractions)}
										disabled={isSaltSubmitting}
									>
										+ {t("Add")}
									</Button>
								</div>
								{interactions.map((interaction, index) => (
									<div className="master-add-list-row" key={`interaction-${index}`}>
										<input
											type="text"
											className="form-control form-control-sm"
											value={interaction}
											onChange={(e) => handleListRowChange(setInteractions, index, e.target.value)}
											disabled={isSaltSubmitting}
											placeholder={t("e.g., Alcohol, Warfarin")}
										/>
										{interactions.length > 1 && (
											<Button
												color="danger"
												size="sm"
												type="button"
												className="master-add-mini-btn"
												onClick={() => removeListRow(setInteractions, index)}
												disabled={isSaltSubmitting}
											>
												{t("Remove")}
											</Button>
										)}
									</div>
								))}
							</div>
						</div>

						{saltModalError && <div className="alert alert-danger dark fade show mt-2 mb-0 py-2">{saltModalError}</div>}
					</ModalBody>
					<ModalFooter className="master-add-modal-footer">
						<Button color="primary" size="sm" type="submit" disabled={isSaltSubmitting}>
							{isSaltSubmitting ? t("Adding...") : t("Add Medicine")}
						</Button>
						<Button color="secondary" size="sm" type="button" onClick={toggleSaltModal} disabled={isSaltSubmitting}>
							{t("Cancel")}
						</Button>
					</ModalFooter>
				</form>
			</Modal>

			{/* OTC Modal */}
			<Modal isOpen={isOTCModalOpen} toggle={toggleOTCModal} size="lg" className="master-add-modal">
				<ModalHeader toggle={toggleOTCModal}>{t("Add New OTC Product")}</ModalHeader>
				<form onSubmit={handleOTCModalSubmit}>
					<ModalBody className="master-add-modal-body">
						<div className="row g-2">
							<div className="col-12">
								<label className="form-label label-with-info">
									{t("Product Name")} <span className="required-mark">*</span>
									<InfoIcon text={t("Required. Enter the OTC product name for the master database.")} />
								</label>
								<input
									type="text"
									name="name"
									className="form-control form-control-sm"
									value={otcModalData.name}
									onChange={handleOTCModalChange}
									disabled={isOTCSubmitting}
									placeholder={t("e.g., Bandage, Protein Powder, Thermometer")}
								/>
							</div>

							<div className="col-md-6">
								<label className="form-label label-with-info">
									{t("Manufacturer")}
									<InfoIcon text={t("Optional. Brand or company manufacturing this OTC item.")} />
								</label>
								<input
									type="text"
									name="manufacturer"
									className="form-control form-control-sm"
									value={otcModalData.manufacturer}
									onChange={handleOTCModalChange}
									disabled={isOTCSubmitting}
									placeholder={t("e.g., Johnson & Johnson, Dr. Trust")}
								/>
							</div>
							<div className="col-md-6">
								<label className="form-label label-with-info">
									{t("Packaging Type")}
									<InfoIcon text={t("Optional. Packaging such as Strip, Bottle, or Box.")} />
								</label>
								<CreatableOptionSelect
									options={otcPackageTypes}
									value={otcModalData.package}
									onChange={(value) =>
										setOtcModalData((prev) => ({ ...prev, package: value }))
									}
									isDisabled={isOTCSubmitting}
									isCompact
									inModal
									placeholder={t("Search or type packaging")}
								/>
							</div>

							<div className="col-12">
								<label className="form-label label-with-info">
									{t("Product Form")}
									<InfoIcon text={t("Optional. Form of the product (Cream, Device, Powder, etc.).")} />
								</label>
								<CreatableOptionSelect
									options={otcProductForms}
									value={otcModalData.productForm}
									onChange={(value) =>
										setOtcModalData((prev) => ({ ...prev, productForm: value }))
									}
									isDisabled={isOTCSubmitting}
									isCompact
									inModal
									placeholder={t("Search or type form")}
								/>
							</div>

							<div className="col-12">
								<label className="form-label label-with-info">
									{t("Notes")}
									<InfoIcon text={t("Optional. Any extra product details for master records.")} />
								</label>
								<textarea
									name="note"
									className="form-control form-control-sm"
									value={otcModalData.note}
									onChange={handleOTCModalChange}
									disabled={isOTCSubmitting}
									rows="2"
									placeholder={t("Additional information about this product...")}
								/>
							</div>
						</div>

						{otcModalError && <div className="alert alert-danger dark fade show mt-2 mb-0 py-2">{otcModalError}</div>}
					</ModalBody>
					<ModalFooter className="master-add-modal-footer">
						<Button color="primary" size="sm" type="submit" disabled={isOTCSubmitting}>
							{isOTCSubmitting ? t("Adding...") : t("Add OTC Product")}
						</Button>
						<Button color="secondary" size="sm" type="button" onClick={toggleOTCModal} disabled={isOTCSubmitting}>
							{t("Cancel")}
						</Button>
					</ModalFooter>
				</form>
			</Modal>
		</Fragment>
	);
};

export default AddItem;